import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { THEMES, FREE_THEMES, CardRenderer, CARD_FONTS } from "../CardThemes";
import { useAuth } from "../AuthContext";
import UpgradeModal from "./UpgradeModal";

const GLOBAL = `
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html, body { overflow-x: hidden; font-family: 'Outfit', sans-serif; background: #080b10; color: #f0f0f0; }
`;

const SAMPLE_CARD = {
    name: "Alexandra Mercer",
    photoUrl: "",
    bannerUrl: "",
    designation: "Creative Director",
    company: "Studio Nexus",
    bio: "Design leader with 10+ years crafting digital experiences. Passionate about the intersection of art and technology.",
    phone: "+1 (415) 820-9930",
    email: "alex@studionexus.co",
    website: "https://studionexus.co",
    location: "San Francisco",
    resumeUrl: "https://example.com/resume.pdf",
    socials: {
        linkedin: "https://linkedin.com/in/alex",
        twitter: "https://twitter.com/alex",
        instagram: "https://instagram.com/alex",
        github: "https://github.com/alex",
        whatsapp: "14158209930",
    },
};

const CATEGORY_META = {
    "Developer":    { label: "Developer",    emoji: "💻" },
    "Designer":     { label: "Designer",     emoji: "🎨" },
    "Executive":    { label: "Executive",    emoji: "👔" },
    "Startup":      { label: "Startup",      emoji: "🚀" },
    "Marketing":    { label: "Marketing",    emoji: "📣" },
    "Finance":      { label: "Finance",      emoji: "💰" },
    "Medical":      { label: "Medical",      emoji: "🏥" },
    "Legal":        { label: "Legal",        emoji: "⚖️" },
    "Education":    { label: "Education",    emoji: "📚" },
    "Science":      { label: "Science",      emoji: "🔬" },
    "Architecture": { label: "Architecture", emoji: "🏛️" },
    "Real Estate":  { label: "Real Estate",  emoji: "🏠" },
    "Photography":  { label: "Photography",  emoji: "📷" },
    "Music":        { label: "Music",        emoji: "🎵" },
    "Creator":      { label: "Creator",      emoji: "🎬" },
    "Fashion":      { label: "Fashion",      emoji: "👗" },
    "Art":          { label: "Art",          emoji: "🖌️" },
    "Sports":       { label: "Sports",       emoji: "⚡" },
    "Culinary":     { label: "Chef",         emoji: "👨‍🍳" },
    "Media":        { label: "Media",        emoji: "📰" },
    "Others":       { label: "Others",       emoji: "✦"  },
};

function getProfessionCategory(theme) {
    const p = theme.profession || "";
    if (!p) return "Others";
    if (p.includes("Developer") || p.includes("Dev")) return "Developer";
    if (p.includes("Designer") || p.includes("Design")) return "Designer";
    if (p.includes("Entrepreneur") || p.includes("Founder")) return "Startup";
    if (p.includes("CEO") || p.includes("Executive")) return "Executive";
    if (p.includes("Marketing")) return "Marketing";
    if (p.includes("Finance") || p.includes("Banker")) return "Finance";
    if (p.includes("Doctor") || p.includes("Medical") || p.includes("Wellness")) return "Medical";
    if (p.includes("Lawyer") || p.includes("Attorney") || p.includes("Legal")) return "Legal";
    if (p.includes("Educator") || p.includes("Teacher") || p.includes("Tutor")) return "Education";
    if (p.includes("Scientist") || p.includes("Researcher") || p.includes("Lab")) return "Science";
    if (p.includes("Architect") || p.includes("Interior")) return "Architecture";
    if (p.includes("Real Estate")) return "Real Estate";
    if (p.includes("Photographer") || p.includes("Photo")) return "Photography";
    if (p.includes("Music") || p.includes("DJ") || p.includes("Singer") || p.includes("Podcast")) return "Music";
    if (p.includes("Content Creator") || p.includes("Video Creator") || p.includes("Creator")) return "Creator";
    if (p.includes("Fashion")) return "Fashion";
    if (p.includes("Artist") || p.includes("Illustrator")) return "Art";
    if (p.includes("Athlete") || p.includes("Trainer") || p.includes("Coach")) return "Sports";
    if (p.includes("Chef") || p.includes("Restaurateur")) return "Culinary";
    if (p.includes("Journalist") || p.includes("Writer") || p.includes("Editor")) return "Media";
    return "Others";
}

const LAYOUT_LABELS = {
    "classic-dark": "Midnight Gold",
    "classic-light": "Clean White",
    photocard: "Photo Card",
    "banner-float": "Banner + Float",
    aurora: "Aurora Mesh",
    cyber: "Terminal / Cyber",
    "warm-sand": "Warm Serif",
    corporate: "Corporate Sidebar",
};

export default function ThemesPage() {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { isPremium } = useAuth();
    const targetPath = routerLocation.state?.targetPath || null;
    const confirmContext = routerLocation.state?.confirmContext || "create";
    const selectionMode = !!targetPath;
    const [selected, setSelected] = useState(routerLocation.state?.preselectedTheme || null);
    const [pendingTheme, setPendingTheme] = useState(null);
    const [filter, setFilter] = useState("all");
    const [category, setCategory] = useState("all");
    const [search, setSearch] = useState("");
    const [showUpgrade, setShowUpgrade] = useState(false);

    const groups = [];
    const seen = new Set();
    THEMES.forEach((theme) => {
        if (!seen.has(theme.layout)) {
            seen.add(theme.layout);
            groups.push(theme.layout);
        }
    });

    const availableCategories = Object.keys(CATEGORY_META).filter(cat =>
        THEMES.some(t => getProfessionCategory(t) === cat)
    );

    const q = search.trim().toLowerCase();
    const filteredThemes = THEMES.filter((theme) => {
        const isFree = FREE_THEMES.includes(theme.id);
        if (filter === "free" && !isFree) return false;
        if (filter === "premium" && isFree) return false;
        if (category !== "all" && getProfessionCategory(theme) !== category) return false;
        if (q) {
            const label = LAYOUT_LABELS[theme.layout] || theme.layout;
            return (
                theme.name.toLowerCase().includes(q) ||
                theme.id.toLowerCase().includes(q) ||
                label.toLowerCase().includes(q) ||
                (isFree ? "free" : "premium").includes(q) ||
                (theme.profession || "").toLowerCase().includes(q) ||
                (theme.tag || "").toLowerCase().includes(q)
            );
        }
        return true;
    });
    const isFiltered = filter !== "all" || q.length > 0 || category !== "all";

    const getThemeName = (themeId) => THEMES.find((theme) => theme.id === themeId)?.name || "this theme";

    const handleThemeClick = (themeId) => {
        setSelected(themeId);
        setPendingTheme(themeId);
    };

    const handleConfirmTheme = () => {
        if (!pendingTheme) return;
        if (!FREE_THEMES.includes(pendingTheme) && !isPremium) {
            setPendingTheme(null);
            setShowUpgrade(true);
            return;
        }
        const destination = targetPath || "/create";
        navigate(destination, {
            state: {
                preselectedTheme: pendingTheme,
                themeConfirmed: true,
            },
        });
    };

    const renderThemeCard = (theme) => {
        const isFree = FREE_THEMES.includes(theme.id);
        const isSelected = selected === theme.id;

        return (
            <div
                key={theme.id}
                onClick={() => handleThemeClick(theme.id)}
                style={{
                    ...s.col,
                    outline: isSelected ? "2.5px solid #0AADA1" : "2.5px solid transparent",
                    boxShadow: isSelected ? "0 0 0 5px rgba(10,173,161,0.15)" : "none",
                }}
            >
                <div style={s.colHeader}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={s.themeName}>{theme.name}</div>
                        <span
                            style={{
                                ...s.tag,
                                background: isFree ? "rgba(34,197,94,0.1)" : "rgba(245,158,11,0.1)",
                                color: isFree ? "#22c55e" : "#f59e0b",
                            }}
                        >
                            {isFree ? "Free" : "Premium"}
                        </span>
                    </div>
                </div>
                <div style={{ padding: "0 0 20px" }}>
                    <CardRenderer themeId={theme.id} cardData={SAMPLE_CARD} onSave={() => {}} saved={false} />
                </div>
                {isSelected && <div style={s.selectedBadge}>Selected</div>}
            </div>
        );
    };

    return (
        <>
            <style>{CARD_FONTS}</style>
            <style>{GLOBAL}</style>
            <div style={s.page}>
                <div style={s.header}>
                    <button onClick={() => navigate(-1)} style={s.backBtn}>{"<"} Back</button>
                    <div style={s.logoText}>myprofilecorner</div>
                </div>

                <div style={s.hero}>
                    <div style={s.heroChip}>Card Themes</div>
                    <h1 style={s.heroTitle}>Choose your <span style={{ color: "#0AADA1" }}>style</span></h1>
                    <p style={s.heroSub}>
                        {selectionMode
                            ? "Select a theme to use on your card. We will confirm before switching it."
                            : `${THEMES.length} unique themes across many different card layouts. Tap to select.`}
                    </p>
                </div>

                {/* Search + Filter */}
                <div style={s.filterBar}>
                    <div style={s.searchWrap}>
                        <span style={s.searchIcon}>🔍</span>
                        <input
                            style={s.searchInput}
                            placeholder="Search themes…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        {search && (
                            <button style={s.clearBtn} onClick={() => setSearch("")}>✕</button>
                        )}
                    </div>
                    <div style={s.chips}>
                        {["all", "free", "premium"].map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                style={{
                                    ...s.chip,
                                    ...(filter === f ? (f === "premium" ? s.chipAmber : s.chipActive) : {}),
                                }}
                            >
                                {f === "all" ? "All" : f === "free" ? "✦ Free" : "⭐ Premium"}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Category filters */}
                <div style={s.categoryBar}>
                    <button
                        onClick={() => setCategory("all")}
                        style={{ ...s.catChip, ...(category === "all" ? s.catChipActive : {}) }}
                    >
                        🌐 All Professions
                    </button>
                    {availableCategories.map(cat => {
                        const meta = CATEGORY_META[cat];
                        return (
                            <button
                                key={cat}
                                onClick={() => setCategory(cat)}
                                style={{ ...s.catChip, ...(category === cat ? s.catChipActive : {}) }}
                            >
                                {meta.emoji} {meta.label}
                            </button>
                        );
                    })}
                </div>

                {/* Filtered flat grid */}
                {isFiltered && (
                    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 28px 48px" }}>
                        {filteredThemes.length === 0 ? (
                            <div style={{ textAlign: "center", padding: "60px 0", color: "rgba(255,255,255,0.25)", fontSize: 15 }}>
                                No themes match your search.
                            </div>
                        ) : (
                            <>
                                <div style={s.sectionHeader}>
                                    <div style={s.sectionLabel}>
                                        {category !== "all" ? `${CATEGORY_META[category]?.emoji ?? ""} ${CATEGORY_META[category]?.label ?? category} · ` : ""}
                                        {filteredThemes.length} theme{filteredThemes.length !== 1 ? "s" : ""}
                                    </div>
                                    <div style={s.sectionLine} />
                                </div>
                                <div style={s.grid}>{filteredThemes.map(renderThemeCard)}</div>
                            </>
                        )}
                    </div>
                )}

                {!isFiltered && (() => {
                    const featuredThemes = ["dark", "light", "photocard"]
                        .map((themeId) => THEMES.find((theme) => theme.id === themeId))
                        .filter(Boolean);

                    return (
                        <div style={s.section}>
                            <div style={s.sectionHeader}>
                                <div style={s.sectionLabel}>Featured</div>
                                <div style={s.sectionLine} />
                            </div>
                            <div style={s.grid}>
                                {featuredThemes.map(renderThemeCard)}
                            </div>
                        </div>
                    );
                })()}

                {!isFiltered && groups
                    .filter((layout) => !["classic-dark", "classic-light", "photocard"].includes(layout))
                    .map((layout) => (
                        <div key={layout} style={s.section}>
                            <div style={s.sectionHeader}>
                                <div style={s.sectionLabel}>{LAYOUT_LABELS[layout] || layout}</div>
                                <div style={s.sectionLine} />
                            </div>
                            <div style={s.grid}>
                                {THEMES.filter((theme) => theme.layout === layout).map(renderThemeCard)}
                            </div>
                        </div>
                    ))}

                <div style={s.cta}>
                    <button
                        style={s.ctaBtn}
                        onClick={() => {
                            if (selected && !FREE_THEMES.includes(selected) && !isPremium) {
                                setShowUpgrade(true);
                                return;
                            }
                            if (selectionMode && targetPath) {
                                navigate(targetPath, {
                                    state: selected ? { preselectedTheme: selected } : undefined,
                                });
                                return;
                            }
                            navigate("/create", {
                                state: selected ? { preselectedTheme: selected } : undefined,
                            });
                        }}
                    >
                        {selectionMode
                            ? (selected ? `Continue with ${getThemeName(selected)}` : "Continue without changing")
                            : (selected ? `Get Started with ${getThemeName(selected)}` : "Get Started Free")}
                    </button>
                    <p style={s.ctaNote}>
                        {selectionMode
                            ? "Tap any theme card to confirm and apply it to your card."
                            : "No credit card required for free themes"}
                    </p>
                </div>

                {pendingTheme && (
                    <div style={s.modalOverlay} onClick={() => setPendingTheme(null)}>
                        <div style={s.modalCard} onClick={(e) => e.stopPropagation()}>
                            <div style={s.modalChip}>Use Theme</div>
                            <h3 style={s.modalTitle}>Use {getThemeName(pendingTheme)}?</h3>
                            <p style={s.modalText}>
                                {targetPath
                                    ? "This will open your edit page with the selected theme ready to preview."
                                    : "This will open your card with the selected theme already applied."}
                            </p>
                            <div style={s.modalActions}>
                                <button style={s.modalCancelBtn} onClick={() => setPendingTheme(null)}>Cancel</button>
                                <button style={s.modalConfirmBtn} onClick={handleConfirmTheme}>OK</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} />}
        </>
    );
}

const s = {
    page: { minHeight: "100vh", background: "#080b10", paddingBottom: "80px" },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "18px 28px",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        position: "sticky",
        top: 0,
        zIndex: 100,
        background: "rgba(8,11,16,0.92)",
        backdropFilter: "blur(12px)",
    },
    backBtn: {
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.1)",
        color: "#ccc",
        borderRadius: "10px",
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: "600",
        cursor: "pointer",
        fontFamily: "'Outfit',sans-serif",
    },
    logoText: { fontSize: "14px", fontWeight: "700", color: "rgba(255,255,255,0.4)", letterSpacing: "0.5px" },
    hero: { textAlign: "center", padding: "60px 24px 48px", maxWidth: "580px", margin: "0 auto" },
    heroChip: {
        display: "inline-block",
        background: "rgba(10,173,161,0.12)",
        color: "#0AADA1",
        fontSize: "11px",
        fontWeight: "700",
        letterSpacing: "1.5px",
        textTransform: "uppercase",
        borderRadius: "100px",
        padding: "5px 16px",
        marginBottom: "20px",
    },
    heroTitle: {
        fontSize: "clamp(2rem,5vw,3rem)",
        fontWeight: "800",
        color: "#ffffff",
        lineHeight: 1.15,
        marginBottom: "14px",
        letterSpacing: "-0.5px",
    },
    heroSub: { fontSize: "15px", color: "rgba(255,255,255,0.45)", lineHeight: 1.7 },
    section: { maxWidth: "1400px", margin: "0 auto", padding: "0 28px 48px" },
    sectionHeader: { display: "flex", alignItems: "center", gap: "14px", marginBottom: "24px" },
    sectionLabel: {
        fontSize: "11px",
        fontWeight: "700",
        color: "rgba(255,255,255,0.35)",
        letterSpacing: "1.5px",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
    },
    sectionLine: { flex: 1, height: "1px", background: "rgba(255,255,255,0.06)" },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: "24px" },
    col: {
        borderRadius: "22px",
        background: "rgba(255,255,255,0.02)",
        border: "1px solid rgba(255,255,255,0.06)",
        overflow: "hidden",
        cursor: "pointer",
        position: "relative",
        transition: "outline 0.15s,box-shadow 0.15s",
    },
    colHeader: { padding: "18px 18px 14px", borderBottom: "1px solid rgba(255,255,255,0.05)", marginBottom: "14px" },
    themeName: { fontSize: "15px", fontWeight: "700", color: "#ffffff" },
    tag: { fontSize: "10px", fontWeight: "700", borderRadius: "100px", padding: "3px 9px", letterSpacing: "0.3px" },
    selectedBadge: {
        position: "absolute",
        top: "14px",
        right: "14px",
        background: "#0AADA1",
        color: "#fff",
        fontSize: "10px",
        fontWeight: "700",
        borderRadius: "100px",
        padding: "4px 10px",
    },
    filterBar: { maxWidth: "1400px", margin: "0 auto", padding: "0 28px 16px", display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" },
    categoryBar: { maxWidth: "1400px", margin: "0 auto", padding: "0 28px 28px", display: "flex", gap: 8, flexWrap: "wrap" },
    catChip: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.45)", borderRadius: 100, padding: "7px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'Outfit',sans-serif", transition: "all 0.15s", whiteSpace: "nowrap", flexShrink: 0 },
    catChipActive: { background: "rgba(10,173,161,0.15)", border: "1px solid rgba(10,173,161,0.4)", color: "#0AADA1" },
    searchWrap: { flex: 1, minWidth: 200, display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.09)", borderRadius: 12, padding: "10px 14px" },
    searchIcon: { fontSize: 14, opacity: 0.5, flexShrink: 0 },
    searchInput: { flex: 1, background: "none", border: "none", outline: "none", color: "#fff", fontSize: 14, fontFamily: "'Outfit',sans-serif", "::placeholder": { color: "rgba(255,255,255,0.25)" } },
    clearBtn: { background: "none", border: "none", color: "rgba(255,255,255,0.3)", cursor: "pointer", fontSize: 13, padding: 0, lineHeight: 1 },
    chips: { display: "flex", gap: 8, flexShrink: 0 },
    chip: { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", color: "rgba(255,255,255,0.4)", borderRadius: 100, padding: "8px 18px", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Outfit',sans-serif", transition: "all 0.15s" },
    chipActive: { background: "rgba(10,173,161,0.15)", border: "1px solid rgba(10,173,161,0.35)", color: "#0AADA1" },
    chipAmber: { background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.3)", color: "#f59e0b" },
    cta: { textAlign: "center", padding: "40px 24px 0" },
    ctaBtn: {
        background: "linear-gradient(135deg,#0AADA1,#0ea89c)",
        color: "#fff",
        border: "none",
        borderRadius: "14px",
        padding: "16px 36px",
        fontSize: "16px",
        fontWeight: "700",
        cursor: "pointer",
        fontFamily: "'Outfit',sans-serif",
        letterSpacing: "-0.2px",
        boxShadow: "0 4px 20px rgba(10,173,161,0.3)",
    },
    ctaNote: { marginTop: "12px", fontSize: "12px", color: "rgba(255,255,255,0.25)" },
    modalOverlay: {
        position: "fixed",
        inset: 0,
        background: "rgba(3,6,10,0.72)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        zIndex: 300,
    },
    modalCard: {
        width: "100%",
        maxWidth: "420px",
        background: "#10151d",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "24px",
        padding: "24px",
        boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
    },
    modalChip: {
        display: "inline-block",
        background: "rgba(10,173,161,0.12)",
        color: "#0AADA1",
        fontSize: "10px",
        fontWeight: "700",
        letterSpacing: "1.5px",
        textTransform: "uppercase",
        borderRadius: "999px",
        padding: "5px 12px",
        marginBottom: "14px",
    },
    modalTitle: { fontSize: "24px", fontWeight: "800", color: "#fff", marginBottom: "10px", lineHeight: 1.15 },
    modalText: { fontSize: "14px", color: "rgba(255,255,255,0.62)", lineHeight: 1.7 },
    modalActions: { display: "flex", gap: "10px", marginTop: "22px" },
    modalCancelBtn: {
        flex: 1,
        background: "rgba(255,255,255,0.04)",
        color: "#d4d8de",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "12px",
        padding: "12px 16px",
        fontSize: "14px",
        fontWeight: "700",
        cursor: "pointer",
    },
    modalConfirmBtn: {
        flex: 1,
        background: "linear-gradient(135deg,#0AADA1,#0ea89c)",
        color: "#fff",
        border: "none",
        borderRadius: "12px",
        padding: "12px 16px",
        fontSize: "14px",
        fontWeight: "800",
        cursor: "pointer",
    },
};
